/**
 * print-e2e (`006` T034/T035) Step 0: this screen previously expected `GET
 * /admin/invoices/{id}/print` to answer pre-rendered HTML (`.html`/`.document`/`.printHtml`/
 * `.renderedHtml`) and fell into a permanent "no printable document yet" state for every real
 * invoice. Verified against the real backend: `AdminInvoiceController.PrintInvoice` calls the same
 * `InvoicePrintHandler.Render(invoice)` as `GetInvoice` and returns `InvoicePrint` — flat fields,
 * never markup (`InvoicePrintHandler.cs`, `packages/api-client/src/billing.d.ts`).
 *
 * Redesigned 2026-10-05 (owner decision): this screen renders `TaxInvoice`
 * (`components/tax-invoice.tsx`) through `lib/invoice-model.ts` — copies of the portal's own print
 * invoice, so the back office prints the same document the customer prints and downloads.
 * `Detail.tsx`'s on-screen document is still `InvoiceDocument` (`@steelyard/theme`), the component
 * `domain/parity.test.tsx` holds to the frozen baseline; the portal split its print route off the
 * same way on 2026-10-01.
 */
import { useEffect } from "react";
import { Link, redirect, useLoaderData, type LoaderFunctionArgs } from "react-router-dom";
import { ApiError } from "../../lib/api";
import { getAccount, getPermissionState, safeReturnTo } from "../../lib/identity";
import { invoiceModel, type InvoicePayment } from "../../lib/invoice-model";
import { can } from "../../lib/permissions";
import { AmbiguousNumberError, getInvoicePrint, invoiceByKey, type InvoiceDetail } from "../../lib/services/invoicing";
import { AmbiguousNumber } from "./number-route-error";
import { invoicePath } from "../../lib/paths";
import { getOrder } from "../../lib/services/ordering";
import { TaxInvoice } from "../../components/tax-invoice";
import { ErrorSurface } from "@steelyard/theme";
import { Button } from "rizzui";
import { PiArrowLeftBold } from "react-icons/pi";
import "../../print.css";

/**
 * Ported from apps/admin/app/invoicing/[id]/print/{page,layout}.tsx. Deliberately outside the
 * app shell — no sidebar, no header, no navigation landmark — for the reason the original states:
 * hiding chrome with `@media print` still leaves it in the DOM, tab order and accessibility tree
 * of a legal document. This route is a top-level sibling of "/", not a child of RootRoute, so it
 * never renders inside `AppShell`. Because of that it does its own account/permission read here
 * rather than inheriting the root loader's.
 *
 * `getInvoicePrint` hits `/print`, not `/{id}` — the real route also writes a `Reprinted` audit row
 * server-side on every call (FR-050), which is the point of using it. A 200 body is always
 * renderable now (there is no "no document" state left to fall into); only `forbidden` and a
 * genuine transport/API error remain.
 */
type PrintLoaderData =
  | { kind: "forbidden" }
  | { kind: "error"; href: string; error: unknown }
  | { kind: "document"; href: string; data: InvoiceDetail; payment: InvoicePayment | null };

export async function printInvoiceLoader({ params, request }: LoaderFunctionArgs): Promise<PrintLoaderData> {
  const key = params.invoiceNumber!;
  const path = new URL(request.url).pathname;

  const account = await getAccount();
  if (!account) throw redirect(`/sign-in?returnTo=${encodeURIComponent(safeReturnTo(path))}`);

  const permissionState = await getPermissionState();
  if (!can(permissionState, "billing.read")) return { kind: "forbidden" };

  try {
    // A legacy id throws a redirect to the number URL (not an ApiError — rethrown below), unless
    // its number is ambiguous; then this id URL is the record's address, so Back keeps it.
    const data = await getInvoicePrint((await invoiceByKey(request, key)).id);
    return { kind: "document", href: invoicePath(key), data, payment: await readPayment(data.orderId) };
  } catch (error) {
    if (error instanceof ApiError || error instanceof AmbiguousNumberError) return { kind: "error", href: invoicePath(key), error };
    throw error;
  }
}

/**
 * Opens the browser's print dialog once the document has painted, and offers it again. The short
 * delay lets the IRN QR and web fonts land first; the cleanup keeps React's dev double-mount from
 * opening two dialogs. Shared with `./CreditNotePrint.tsx`.
 */
export function PrintNow() {
  useEffect(() => {
    const timer = setTimeout(() => window.print(), 300);
    return () => clearTimeout(timer);
  }, []);
  return (
    <Button type="button" size="sm" onClick={() => window.print()}>
      Print
    </Button>
  );
}

/**
 * The order's payment mode and status for "Order & payment", as the portal's print route reads them.
 * `billing.read` does not imply `orders.read`, so a refused or failed read leaves those facts out
 * instead of failing the print.
 */
async function readPayment(orderId: string | null): Promise<InvoicePayment | null> {
  if (!orderId) return null;
  try {
    const order = await getOrder(orderId);
    return order ? { orderType: order.orderType, paymentStatus: order.paymentStatus } : null;
  } catch {
    return null;
  }
}

const BACK_TO_INVOICE = (href: string) => (
  <Link to={href}>
    <Button as="span" variant="outline" size="sm">
      Back to the invoice
    </Button>
  </Link>
);

export default function PrintInvoicePage() {
  const data = useLoaderData() as PrintLoaderData;

  if (data.kind === "document") {
    // D-40's DRAFT banner is part of the document (`TaxInvoice`), so it prints too, on purpose.
    return (
      <div className="sy-tinv-page">
        <TaxInvoice model={invoiceModel(data.data, data.payment)}>
          <Link to={data.href} className="sy-tinv-back">
            <PiArrowLeftBold aria-hidden />
            Back to the invoice
          </Link>
          <span className="sy-tinv-toolbar-actions">
            <PrintNow />
          </span>
        </TaxInvoice>
      </div>
    );
  }

  if (data.kind === "error" && data.error instanceof AmbiguousNumberError) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <AmbiguousNumber error={data.error} />
      </div>
    );
  }

  const error =
    data.kind === "forbidden"
      ? { title: "Not permitted", status: 403, detail: "Printing an invoice is gated by billing.read." }
      : data.error;

  return (
    <div className="mx-auto max-w-2xl p-6">
      <ErrorSurface error={error} action="print this invoice">
        {data.kind === "forbidden" ? null : BACK_TO_INVOICE(data.href)}
      </ErrorSurface>
    </div>
  );
}
